import { Link } from 'react-router-dom';

function Placeholder({ children }: { children: string }) {
  return (
    <span className="bg-yellow-100 text-yellow-800 px-1 rounded font-mono text-sm">
      [{children}]
    </span>
  );
}

export function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-cream-100">
      <header className="bg-white border-b border-cream-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="font-fraunces text-2xl font-light tracking-tight text-navy-mid">
            <em className="font-medium text-primary-500" style={{ fontStyle: 'italic' }}>Kin</em>Care
          </Link>
          <Link to="/" className="text-sm text-primary-600 hover:text-primary-700">
            Back to home
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
        <p className="text-sm text-gray-500 mb-10">
          Last updated: <Placeholder>DATE</Placeholder>
        </p>

        <section className="space-y-4 text-gray-700 leading-relaxed">
          <h2 className="text-xl font-semibold text-gray-900 mt-10 mb-4">1. Introduction</h2>
          <p>
            <Placeholder>COMPANY NAME</Placeholder> (&quot;we&quot;, &quot;us&quot;, or &quot;our&quot;) operates the KinCare
            platform, a family care coordination service. This Privacy Policy explains how we collect,
            use, disclose, and safeguard your information when you use our platform.
          </p>
          <p>
            We are committed to protecting the privacy and security of our users. Your trust is
            fundamental to everything we do. We will never sell your data or use it for advertising.
          </p>

          <h2 className="text-xl font-semibold text-gray-900 mt-10 mb-4">
            2. Information We Collect
          </h2>
          <h3 className="text-lg font-medium text-gray-800 mt-6 mb-3">Account Information</h3>
          <p>
            When you create an account, we collect your name, email address, and authentication
            credentials (managed securely via our authentication provider, Clerk).
          </p>
          <h3 className="text-lg font-medium text-gray-800 mt-6 mb-3">Care Information</h3>
          <p>
            Information you provide about your family members and dependents, including names, dates
            of birth, medical conditions, medications, appointments, allergies, and emergency
            contacts. This is the core data that enables care coordination.
          </p>
          <h3 className="text-lg font-medium text-gray-800 mt-6 mb-3">Usage Data</h3>
          <p>
            We automatically collect certain information when you access our platform, including
            device type, browser type, IP address, pages visited, and interaction patterns. This
            helps us improve the service. <Placeholder>DETAIL ANALYTICS PROVIDER IF APPLICABLE</Placeholder>
          </p>

          <h2 className="text-xl font-semibold text-gray-900 mt-10 mb-4">
            3. How We Use Your Information
          </h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>To provide and maintain the KinCare care coordination platform</li>
            <li>To facilitate care coordination between family members within your care group</li>
            <li>To send you notifications about tasks, medications, and appointments</li>
            <li>To improve and personalise your experience</li>
            <li>To ensure the security and integrity of our platform</li>
            <li>To comply with legal obligations</li>
          </ul>

          <h2 className="text-xl font-semibold text-gray-900 mt-10 mb-4">4. Data Sharing</h2>
          <p>
            We share your information only in the following limited circumstances:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong>Within your family group:</strong> Information you add to a family is visible
              to other members of that family, according to their role permissions.
            </li>
            <li>
              <strong>Service providers:</strong> We use trusted third-party services to operate our
              platform, including Clerk (authentication){' '}
              <Placeholder>LIST OTHER PROCESSORS e.g. hosting provider, email service</Placeholder>.
              These providers are contractually obligated to protect your data.
            </li>
            <li>
              <strong>Legal requirements:</strong> We may disclose information if required by law,
              court order, or governmental regulation.
            </li>
          </ul>
          <p className="font-medium">
            We will never sell your personal data to third parties. We do not serve advertisements.
          </p>

          <h2 className="text-xl font-semibold text-gray-900 mt-10 mb-4">5. Data Retention</h2>
          <p>
            We retain your personal data for as long as your account is active or as needed to
            provide you services. If you delete your account, we will delete or anonymise your
            personal data within <Placeholder>RETENTION PERIOD, e.g. 30 days</Placeholder>, except
            where we are required to retain it for legal or regulatory purposes.
          </p>

          <h2 className="text-xl font-semibold text-gray-900 mt-10 mb-4">
            6. Your Rights (GDPR)
          </h2>
          <p>
            If you are located in the European Economic Area (EEA) or the United Kingdom, you have
            the following rights under the General Data Protection Regulation (GDPR):
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong>Right of access:</strong> Request a copy of the personal data we hold about
              you.
            </li>
            <li>
              <strong>Right to rectification:</strong> Request correction of inaccurate or incomplete
              data.
            </li>
            <li>
              <strong>Right to erasure:</strong> Request deletion of your personal data.
            </li>
            <li>
              <strong>Right to data portability:</strong> Request your data in a structured,
              machine-readable format.
            </li>
            <li>
              <strong>Right to restriction:</strong> Request that we limit processing of your data.
            </li>
            <li>
              <strong>Right to object:</strong> Object to processing of your data for certain
              purposes.
            </li>
          </ul>
          <p>
            To exercise any of these rights, please contact us at{' '}
            <Placeholder>PRIVACY EMAIL ADDRESS</Placeholder>.
          </p>

          <h2 className="text-xl font-semibold text-gray-900 mt-10 mb-4">
            7. Children&apos;s and Dependents&apos; Privacy
          </h2>
          <p>
            KinCare is designed for adults coordinating care for family members. We do not knowingly
            collect personal information directly from children under 16. Information about
            dependents (including children) is provided by authorised adult family members for the
            purpose of care coordination.
          </p>

          <h2 className="text-xl font-semibold text-gray-900 mt-10 mb-4">8. Security</h2>
          <p>
            We implement appropriate technical and organisational measures to protect your personal
            data, including encryption in transit and at rest, access controls, and regular security
            reviews. However, no method of electronic storage is 100% secure, and we cannot
            guarantee absolute security.
          </p>

          <h2 className="text-xl font-semibold text-gray-900 mt-10 mb-4">
            9. Changes to This Policy
          </h2>
          <p>
            We may update this Privacy Policy from time to time. We will notify you of any material
            changes by posting the new policy on this page and updating the &quot;Last updated&quot;
            date. We encourage you to review this policy periodically.
          </p>

          <h2 className="text-xl font-semibold text-gray-900 mt-10 mb-4">10. Contact Us</h2>
          <p>
            If you have any questions about this Privacy Policy or our data practices, please
            contact us:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              Email: <Placeholder>PRIVACY EMAIL ADDRESS</Placeholder>
            </li>
            <li>
              Address: <Placeholder>COMPANY REGISTERED ADDRESS</Placeholder>
            </li>
            <li>
              Data Protection Officer: <Placeholder>DPO NAME AND CONTACT IF APPLICABLE</Placeholder>
            </li>
          </ul>
        </section>
      </main>

      <footer className="border-t border-cream-200 mt-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-wrap gap-6 text-sm text-gray-500">
          <Link to="/privacy" className="hover:text-gray-700">
            Privacy Policy
          </Link>
          <Link to="/cookies" className="hover:text-gray-700">
            Cookie Policy
          </Link>
          <Link to="/terms" className="hover:text-gray-700">
            Terms of Service
          </Link>
        </div>
      </footer>
    </div>
  );
}
